import { atom, useRecoilState } from "recoil";

import { API_FETCH_STATUS } from "constants/common";
import { checkSession, fetchUserProfile, downloadPicture } from "./services/profile";
import { INITIAL_STATE, PICTURE_TYPE } from "./constants/constants";
import { checkContactType, getImageUrl } from "./utils/utils";

const profileAtom = atom({
  key: "profile",
  default: INITIAL_STATE,
});

const { LOADING, SUCCESS, ERROR } = API_FETCH_STATUS;

const useProfile = () => {
  const [state, setState] = useRecoilState(profileAtom);
  const { isAuth, loginId, profile, profilePictureUrl, clientLogoUrl } = state;

  const updateStateProperties = (newState) => setState((prevState) => ({ ...prevState, ...newState }));

  const setProfile = (updatedProperties) => {
    return updateStateProperties({
      profile: {
        ...profile,
        contents: {
          ...profile.contents,
          ...updatedProperties,
        },
      },
    });
  };

  const checkUserSession = () => {

    if (localStorage.getItem("accessToken") && localStorage.getItem("refreshToken")) {
      return checkSession()
        .then((res) => {
          if (res.data?.loginId) {
            updateStateProperties({ isAuth: true, loginId: res.data.loginId });
            return (res.data);
          } else {
            throw res;
          }
        })
        .catch((error) => {
          if (error.response?.status === 401) {
            updateStateProperties({ isAuth: false });
          } else {
            updateStateProperties({ isAuth: INITIAL_STATE.isAuth });
          }
          return error;
        });
    }
    else {
      updateStateProperties({ isAuth: false });
      return new Promise((resolve) => {
        resolve({ isAuth: false, loginId: "" })
      })
    }
  };

  const fetchProfile = () => {
    updateStateProperties({ profile: { ...profile, state: LOADING } });

    return new Promise(async (resolve, reject) => {
      try {
        const profileResponse = await fetchUserProfile();
        if (profileResponse.data && !profileResponse.data.code) {
          const isInternalUser = profileResponse.data.isReadOnlySuperAdminLogin
            ? profileResponse.data.isSecondaryUserInternalUser
            : profileResponse.data.isInternalUser;

          window.isInternalUser = isInternalUser;

          const isUserVrmTL = checkContactType(profileResponse.data.contactTypesForLob, "VRM_TL");
          const isKAM = checkContactType(profileResponse.data.contactTypesForLob, "KAM");

          const contents = {
            ...profileResponse.data,
            isUserVrmTL,
            isKAM,
            isInternalUser,
          };

          updateStateProperties({
            profile: {
              state: SUCCESS,
              contents
            },
            isAuth: true,
            loginId: profileResponse.data.ivUser
          });

          if (profileResponse.data.s3ProfilePicUrlPresent) {
            downloadProfilePicture();
          } else {
            updateStateProperties({ profilePictureUrl: profileResponse.data.profilePictureUrl });
          }

          if (profileResponse.data.s3ClientLogoUrlPresent) {
            downloadClientPicture();
          } else {
            updateStateProperties({ clientLogoUrl: profileResponse.data.clientLogoUrl });
          }

          resolve(contents);
        } else {
          throw new Error(profileResponse);
        }
      } catch (error) {
        updateStateProperties({
          profile: {
            state: ERROR,
            contents: INITIAL_STATE.profile.contents,
          },
          isAuth: true,
          loginId: ""
        });
        reject(error);
      }
    });
  };

  const clearProfile = () => updateStateProperties({ profile: { ...INITIAL_STATE.profile } });

  const downloadProfilePicture = () => {
    return downloadPicture({ picType: PICTURE_TYPE.PROFILE_PIC }).then(res => {
      const profilePictureUrl = res.data ? getImageUrl(res.data) : null;
      updateStateProperties({ profilePictureUrl });
      return res;
    });
  };

  const downloadClientPicture = () => {
    return downloadPicture({ picType: PICTURE_TYPE.COMPANY_PIC }).then(res => {
      const clientLogoUrl = res.data ? getImageUrl(res.data) : null;
      updateStateProperties({ clientLogoUrl });
      return res;
    });
  };

  return {
    isAuth,
    loginId,
    profilePictureUrl,
    downloadProfilePicture,
    clientLogoUrl,
    downloadClientPicture,
    checkUserSession,
    profile,
    fetchProfile,
    setProfile,
    clearProfile,
  };
};

export default useProfile;
